// Admin enrollment inquiries — Phase 3 Module 11 · data layer.
//
// All CRUD runs through the existing RLS policies: admin SELECT / UPDATE
// scoped to the caller's organization (migration 20260803000000).
// Stats come from rpc_inquiry_stats (migration 20260804000000) to keep
// the dashboard query off the `enrollment_inquiries` table path.

import { supabase, isSupabaseConfigured } from "./supabase";

const COLS =
  "id, parent_name, parent_email, parent_phone, child_age, message, " +
  "status, notes, assigned_to, source, metadata, created_at, updated_at";

/**
 * List inquiries for the caller's org, newest first.
 * Optional filters: status ('new'|'contacted'|'archived'), search (by
 * name/email ILIKE substring), assignedTo (profile uuid — use 'me' shortcut
 * from callers that have the auth profile, or a full uuid), limit.
 */
export async function listInquiries({ status = null, search = null, assignedTo = null, limit = 100 } = {}) {
  if (!isSupabaseConfigured) return [];
  let q = supabase
    .from("enrollment_inquiries")
    .select(COLS)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (status) q = q.eq("status", status);
  if (assignedTo) q = q.eq("assigned_to", assignedTo);
  if (search && search.trim().length > 1) {
    const s = `%${search.trim().replace(/[%_]/g, "\\$&")}%`;
    // name OR email OR message substring match.
    q = q.or(`parent_name.ilike.${s},parent_email.ilike.${s},message.ilike.${s}`);
  }
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getInquiry(id) {
  if (!isSupabaseConfigured || !id) return null;
  const { data, error } = await supabase
    .from("enrollment_inquiries")
    .select(COLS)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateInquiryStatus(id, status) {
  if (!["new", "contacted", "archived"].includes(status)) {
    throw new Error(`Invalid status: ${status}`);
  }
  const { data, error } = await supabase
    .from("enrollment_inquiries")
    .update({ status })
    .eq("id", id)
    .select(COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateInquiryNotes(id, notes) {
  const { data, error } = await supabase
    .from("enrollment_inquiries")
    .update({ notes: notes ?? "" })
    .eq("id", id)
    .select(COLS)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Assign an inquiry to an admin, or unassign by passing null.
 * Mirrors updateInquiryStatus / updateInquiryNotes — relies on the
 * existing admin UPDATE RLS policy on enrollment_inquiries.
 */
export async function updateInquiryAssignee(id, assigneeId) {
  const { data, error } = await supabase
    .from("enrollment_inquiries")
    .update({ assigned_to: assigneeId || null })
    .eq("id", id)
    .select(COLS)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Lists the admin profiles in the caller's org. Wraps rpc_list_admin_recipients
 * (shipped with Module 10 for marketing fan-out) — we reuse it here so we don't
 * need another migration. Requires the admin's organization_id from AuthContext
 * profile. Falls back to [] on error so the picker degrades to "Unassigned only".
 */
export async function listOrgAdmins(organizationId) {
  if (!isSupabaseConfigured || !organizationId) return [];
  const { data, error } = await supabase.rpc("rpc_list_admin_recipients", {
    p_organization_id: organizationId,
  });
  if (error) {
    // Defensive default; surface only at debug level.
    // eslint-disable-next-line no-console
    console.debug("[inquiries] listOrgAdmins fallback to []:", error.message);
    return [];
  }
  return (data || []).map((r) => ({
    profile_id: r.profile_id,
    email: r.email,
    full_name: r.full_name || r.email?.split("@")[0] || "Admin",
  }));
}

export async function getInquiryStats() {
  if (!isSupabaseConfigured) {
    return { total: 0, new_count: 0, contacted_count: 0, archived_count: 0, this_week: 0 };
  }
  const { data, error } = await supabase.rpc("rpc_inquiry_stats", {});
  if (error) throw error;
  return data || {};
}

// UI helpers -----------------------------------------------------------------
export const INQUIRY_STATUSES = [
  { key: "new",       label: "New",       tone: "border-primary/30 bg-primary/10 text-primary-hover" },
  { key: "contacted", label: "Contacted", tone: "border-[#EFBA57]/40 bg-[#EFBA57]/15 text-[#8A5A12]" },
  { key: "archived",  label: "Archived",  tone: "border-neutral-300 bg-neutral-100 text-neutral-600" },
];

export function statusMeta(key) {
  return INQUIRY_STATUSES.find((s) => s.key === key) || INQUIRY_STATUSES[0];
}
